import type { Lang } from './languages.js';
import { uniqueLangs } from './languages.js';
import type { RuleSet } from './rules.js';

export const MODE_IDS = ['classic', 'polyglot', 'duel', 'sprint', 'relax'] as const;
export type ModeId = (typeof MODE_IDS)[number];

/**
 * How languages are chosen:
 * - shared: one set of languages imposed on every participant;
 * - individual: each player locks their own 1–3 languages before the draw is revealed.
 */
export type LanguagePolicy = 'shared' | 'individual';

/** Language bonus family applied by the mode. */
export type LanguageBonusKind = 'none' | 'shared_diversity' | 'contract';

export interface ModeDef {
  id: ModeId;
  languagePolicy: LanguagePolicy;
  minLanguages: number;
  maxLanguages: number;
  timed: boolean;
  /** Round ends as soon as this many valid distinct words are accepted (Duel). */
  targetWords: 'duel' | null;
  languageBonus: LanguageBonusKind;
  speedBonus: boolean;
  /** Eligible for official/experimental leaderboards (never for offline or bot games). */
  leaderboard: 'official' | 'experimental' | 'none';
}

export const MODES: Readonly<Record<ModeId, ModeDef>> = Object.freeze({
  classic: {
    id: 'classic',
    languagePolicy: 'shared',
    minLanguages: 1,
    maxLanguages: 1,
    timed: true,
    targetWords: null,
    languageBonus: 'none',
    speedBonus: false,
    leaderboard: 'official',
  },
  polyglot: {
    id: 'polyglot',
    languagePolicy: 'shared',
    minLanguages: 2,
    maxLanguages: 3,
    timed: true,
    targetWords: null,
    languageBonus: 'shared_diversity',
    speedBonus: false,
    leaderboard: 'official',
  },
  duel: {
    id: 'duel',
    languagePolicy: 'individual',
    minLanguages: 1,
    maxLanguages: 3,
    timed: true,
    targetWords: 'duel',
    languageBonus: 'contract',
    speedBonus: true,
    leaderboard: 'experimental',
  },
  sprint: {
    id: 'sprint',
    languagePolicy: 'individual',
    minLanguages: 1,
    maxLanguages: 3,
    timed: true,
    targetWords: null,
    languageBonus: 'contract',
    speedBonus: false,
    leaderboard: 'experimental',
  },
  relax: {
    id: 'relax',
    languagePolicy: 'individual',
    minLanguages: 1,
    maxLanguages: 3,
    timed: false,
    targetWords: null,
    languageBonus: 'none',
    speedBonus: false,
    leaderboard: 'none',
  },
}) as Readonly<Record<ModeId, ModeDef>>;

export function isModeId(v: unknown): v is ModeId {
  return typeof v === 'string' && (MODE_IDS as readonly string[]).includes(v);
}

/** Contract of an individual-choice mode, derived from the number of chosen languages. */
export type ContractId = 'specialist' | 'duo' | 'trio';

export function contractForLanguageCount(n: number): ContractId {
  if (n === 1) return 'specialist';
  if (n === 2) return 'duo';
  if (n === 3) return 'trio';
  throw new Error(`Invalid language count for a contract: ${n}`);
}

/**
 * Language requirement of a player's configuration, used for scoring and draw checks.
 * - bonusBp: bonus if fulfilled; quota: words per language required (0 = none).
 */
export interface LanguageRequirement {
  kind: LanguageBonusKind;
  contract: ContractId | null;
  bonusBp: number;
  quotaPerLanguage: number;
}

export function languageRequirement(
  mode: ModeDef,
  langs: readonly Lang[],
  rules: RuleSet,
): LanguageRequirement {
  const n = uniqueLangs(langs).length;
  switch (mode.languageBonus) {
    case 'none':
      return { kind: 'none', contract: null, bonusBp: 0, quotaPerLanguage: 0 };
    case 'shared_diversity': {
      const bonusBp =
        n === 2
          ? rules.sharedDiversity.twoLanguagesBonusBp
          : n === 3
            ? rules.sharedDiversity.threeLanguagesBonusBp
            : 0;
      return {
        kind: 'shared_diversity',
        contract: null,
        bonusBp,
        quotaPerLanguage: n >= 2 ? rules.sharedDiversity.quotaPerLanguage : 0,
      };
    }
    case 'contract': {
      const contract = contractForLanguageCount(n);
      const bonusBp =
        contract === 'specialist'
          ? rules.contract.specialistBonusBp
          : contract === 'duo'
            ? rules.contract.duoBonusBp
            : rules.contract.trioBonusBp;
      return {
        kind: 'contract',
        contract,
        bonusBp,
        quotaPerLanguage: contract === 'specialist' ? 0 : rules.contract.quotaPerLanguage,
      };
    }
  }
}

/** Validates a language selection for a mode; returns an error code or null. */
export function validateLanguageSelection(mode: ModeDef, langs: readonly Lang[]): string | null {
  const u = uniqueLangs(langs);
  if (u.length !== langs.length) return 'duplicate_language';
  if (u.length < mode.minLanguages) return 'too_few_languages';
  if (u.length > mode.maxLanguages) return 'too_many_languages';
  return null;
}

export function targetWordsFor(mode: ModeDef, rules: RuleSet): number | null {
  return mode.targetWords === 'duel' ? rules.duelTargetWords : null;
}
